import { IEverbrightLocaleMessages } from "./IEverbrightLocaleMessages";

/**
 * Vue-i18n locale object to store messages for English.
 * See EverbrightLocale.ts before adding anything here.
 */
export const englishMessages: IEverbrightLocaleMessages = {
    "common": {
        "next": "Next",
        "all-rights-reserved": "All Rights Reserved",
        "espired": "eSpired",
        "contact-us": "Contact Us",
        "terms-of-use": "Terms of Use",
        "privacy-policy": "Privacy Policy",
        "corporate-site": "eSpired Corporate Site",
        "yes": "Yes",
        "no": "No",
        "ok": "OK",
        "credit-card": "Credit Card",
        "debit-card": "Debit Card"
    },

    "step1": {
        header: "Step 1 of 2: Enter your email address"
    },

    "step2": {
        "shipping-information": "Shipping Information",

        "billing-information": "Billing Information",

        "agree-to-terms":
            "You must agree to the terms & conditions in order to continue",

        "what-is-this": "What is this?",

        "terms-and-conditions-header": "Terms & Conditions of this Offer",

        "payment-information": "Payment Information",

        "order-processing-description":
            "Please do not close your browser or refresh the page while your order is processing as it may interfere with the success of your order.",

        "order-processing-time":
            "Processing time may take 10 - 15 seconds.",

        "invalid-shipping-address":
            "You have entered an invalid shipping address",

        "detected-different-address":
            "We have detected a different format for the shipping address you entered:",

        "you-entered": "You Entered:",

        "updated-address": "Updated Address:",

        "use-updated-address": "Would you like to use the updated address?",

        "process-order": "Process Order",

        "invalid-credit-card": "This card number is not valid.",

        "credit-card-expired": "This card has expired."
    },

    "organizationSponsorshipStep2": {
        "shipping-information": "Shipping Information for School/Organization",

        "billing-information": "Sponsor Billing Information",
    },

    "shopping-cart": {
        "header": "Your Shopping Cart",
        "order-total": "Order Total",
        "order-tax": "Tax",
        "order-shipping": "Shipping",
        "cost-free": "FREE",
        "your-order-detail": "Your Order Detail",
        "items-pending-approval": "Items Pending Approval",
        "shipping-and-processing": "Shipping & Processing ◊",
        "order-number": "Order #",
        "free-just-pay-shipping": "Free (Just Pay Shipping & Processing ◊)",
        "shipping-processing-free": "(Shipping & Processing is Free ◊)",

        "all-soft-declined": (messageContext): string =>
            `<p> Thank you for your interest. </p><p> We have encountered an issue with your credit card, but we will continue to make future attempts to process your order. If we are successful, we will send you a confirmation email. </p><p> If you have any questions, please call ${messageContext.named(
                "brandCustomerServicePhone"
            )}. </p>`,

        "some-soft-declined": (messageContext): string =>
            `<p> We have encountered an issue with your credit card, and we are unable to charge certain items in your order, as listed below. </p><p> We will continue make future attempts to process the remaining items in your order. If we are successful, we will send you a confirmation email. </p><p> If you have any questions, please call ${messageContext.named(
                "brandCustomerServicePhone"
            )}. </p>`
    },

    "cvv-modal": {
        header: "What is a CVV Code?",
        text:
            "<p> The CVV code is your card security ID number (also called a \"card verification code\" or \"security code\"), a three- or four-digit number printed on most major credit cards and debit cards. </p><h5>Why do we request card security IDs?</h5> <p> We request your card security ID number to enhance the security of your transaction. The ID number appears on the card itself, and by requesting this number, we are helping to ensure that the cardholder is actually holding the card. This practice reduces the chance that an unauthorized person is fraudulently using your credit or debit card. We do not permanently store security ID numbers in our system. We will request the ID number whenever a customer uses a credit or debit card to purchase items online. </p><h5>Where can I find the card security ID?</h5> <p> You can find the the Card Security Code on the back of MasterCard, Visa and Discover credit or debit cards. It is a separate group of 3 digits on or to the right of the signature strip. On American Express cards, the Card Security Code is a printed (not embossed) group of 4 digits on the front towards the right. </p><h5>What if my card doesn't have a card security ID?</h5> <p> Not all credit and debit cards have a card security ID number. If your card does not have one, please leave the 'Card Security Code' text box blank. </p><h5>What if I can't read my card security ID?</h5> <p> If your card has a card security ID number but you cannot read it, you cannot use that card to complete your transaction. Please contact your credit or debit card issuer for assistance with the problematic card, or use another card. </p>"
    },

    "upsell-modal": {
        yes: "Yes, please add this to my order"
    },

    "promotionOrderModel": {
        shippingFirstName: "Recipient First Name",
        shippingLastName: "Recipient Last Name",
        shippingAddress: "Recipient Street Address",
        shippingAddress2: "Recipient Street Address 2",
        shippingCity: "Recipient City",
        shippingState: "Recipient State",
        shippingZipCode: "Recipient Zip Code",
        shippingPhone: "Recipient Phone",
        billingFirstName: "First Name",
        billingLastName: "Last Name",
        billingAddress: "Billing Street Address",
        billingAddress2: "Billing Street Address 2",
        billingCity: "City",
        billingState: "State",
        billingZipCode: "Zip Code",
        billingPhone: "Phone",
        acceptedTermsAndConditions:
            "I agree I am over the age of 18 and have read and agree to the terms and conditions of the offer above.",
        paymentMethod: "Select Payment Method",
        cardNumber: "Card Number",
        expirationDateMonth: "Exp Month",
        expirationDateYear: "Exp Year",
        cvvCode: "CVV Code",
        billingAddressSameAsShipping:
            "Is your billing address the same as your shipping address?",
        repId: "Rep ID",
        email: "Email"
    },

    "organizationSponsorshipPromotionOrderModel": {
        shippingFirstName: "Recipient School/Organization",
        shippingAddress: "Street Address",
        shippingAddress2: "Contact Name",
        shippingCity: "City",
        shippingState: "State",
        shippingZipCode: "Zip Code",
        shippingPhone: "Phone"
    },

    "validation": {
        // This is a section for vee-validate's validation messages.
        // Validation messages are checked for here first, if they're not found the default one from vee-validate's locale is used.
        //
        // Since this is English and no changes are needed, we can default to all of vee-validate's messages, so nothing is required here.
    },

    "server-validation": {
        promotionOrderModel: {
            cardNumber: "'Card Number' is not a valid credit card number"
        },
        maxMindInvalidOrder:
            "Please correct your information and resubmit your order. (MM)"
    }
};
